import React, { memo } from 'react';
import {
  Box,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, getWeekOfMonth } from 'date-fns';
import { ja } from 'date-fns/locale';
import type { ViewMode } from '../../hooks/useUrlState';

/**
 * NavigationControls Props
 */
interface NavigationControlsProps {
  /** 選択中の日付 */
  selectedDate: Date;
  /** 表示モード */
  viewMode: ViewMode;
  /** 日付変更ハンドラー */
  onDateChange: (date: Date) => void;
  /** 表示モード変更ハンドラー */
  onViewModeChange: (mode: ViewMode) => void;
  /** 次の期間へ移動ハンドラー */
  onNext: () => void;
  /** 前の期間へ移動ハンドラー */
  onPrevious: () => void;
  /** 今日へ移動ハンドラー */
  onToday: () => void;
}

/**
 * 表示モードのラベル
 */
const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  day: '日',
  week: '週',
  month: '月',
  slotSummaryWeek: '枠集計(週)',
  slotSummaryMonth: '枠集計(月)',
};

/**
 * 期間表示テキストを取得
 */
const getPeriodText = (date: Date, viewMode: ViewMode): string => {
  switch (viewMode) {
    case 'day':
      return format(date, 'yyyy年M月d日(E)', { locale: ja });
    case 'week':
    case 'slotSummaryWeek': {
      const weekOfMonth = getWeekOfMonth(date, { weekStartsOn: 1 });
      return `${format(date, 'yyyy年M月', { locale: ja })} 第${weekOfMonth}週`;
    }
    case 'month':
    case 'slotSummaryMonth':
      return format(date, 'yyyy年M月', { locale: ja });
  }
};

/**
 * ナビゲーションコントロールコンポーネント
 *
 * @description
 * 日付ピッカー、前後ナビゲーション、今日ボタン、表示モード切り替えを提供する。
 *
 * @param {NavigationControlsProps} props - コンポーネントProps
 * @returns {React.ReactElement} ナビゲーションコントロール
 *
 * @example
 * <NavigationControls
 *   selectedDate={selectedDate}
 *   viewMode={viewMode}
 *   onDateChange={setSelectedDate}
 *   onViewModeChange={setViewMode}
 *   onNext={goToNext}
 *   onPrevious={goToPrevious}
 *   onToday={goToToday}
 * />
 */
const NavigationControls: React.FC<NavigationControlsProps> = memo(
  ({
    selectedDate,
    viewMode,
    onDateChange,
    onViewModeChange,
    onNext,
    onPrevious,
    onToday,
  }) => {
    /**
     * 表示モード変更ハンドラー
     */
    const handleViewModeChange = (
      _event: React.MouseEvent<HTMLElement>,
      newValue: ViewMode | null
    ) => {
      if (newValue !== null) {
        onViewModeChange(newValue);
      }
    };

    /**
     * 日付ピッカー変更ハンドラー
     */
    const handleDatePickerChange = (date: Date | null) => {
      if (date) {
        onDateChange(date);
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* 前へ/次へナビゲーション */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="前の期間">
            <IconButton onClick={onPrevious} size="small">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="次の期間">
            <IconButton onClick={onNext} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 今日ボタン */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<TodayIcon />}
          onClick={onToday}
        >
          今日
        </Button>

        {/* 日付ピッカー */}
        <DatePicker
          value={selectedDate}
          onChange={handleDatePickerChange}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 180 },
            },
          }}
          format="yyyy/MM/dd"
        />

        {/* 期間表示 */}
        <Typography variant="h6" sx={{ minWidth: 200 }}>
          {getPeriodText(selectedDate, viewMode)}
        </Typography>

        {/* 表示モード切り替え */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          aria-label="表示モード"
        >
          {(Object.keys(VIEW_MODE_LABELS) as ViewMode[]).map((mode) => (
            <ToggleButton key={mode} value={mode} aria-label={VIEW_MODE_LABELS[mode]}>
              {VIEW_MODE_LABELS[mode]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    );
  }
);

NavigationControls.displayName = 'NavigationControls';

export { NavigationControls };
